import { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

import { CheckOutlined, ClearOutlined } from '@mui/icons-material';

import {
  panelSx,
  scrollContainerSx,
  templatesTableSx,
  headerCellSx,
  headerTitleSx,
  addRowSx,
  addInputCellSx,
  inputSx,
  addActionCellSx,
  actionWrapperSx,
  actionIconSx,
  addIconSx,
  clearIconSx,
  actionSvgSx,
  emptyCellSx
} from '../Styles/InputStyles';

import TemplateRow from './TemplateRow';

const ACTION_COLUMN_WIDTH = 82;

const TemplatesPanel = ({
  topics,
  selectedTopicId,
  onSelectTopic,
  onAddTopic,
  onUpdateTopic
}) => {
  const [newTopic, setNewTopic] = useState('');
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editingTopicValue, setEditingTopicValue] = useState('');

  const handleAdd = () => {
    const value = newTopic.trim();

    if (!value) return;

    onAddTopic(value);
    setNewTopic('');
  };

  const handleClear = () => {
    setNewTopic('');
  };

  const handleStartEdit = topic => {
    setEditingTopicId(topic.id);
    setEditingTopicValue(topic.title);
  };

  const handleSaveEdit = topicId => {
    const value = editingTopicValue.trim();

    if (!value) return;

    onUpdateTopic(topicId, value);
    setEditingTopicId(null);
    setEditingTopicValue('');
  };

  const handleCancelEdit = () => {
    setEditingTopicId(null);
    setEditingTopicValue('');
  };

  return (
    <Box sx={panelSx}>
      <TableContainer sx={scrollContainerSx}>
        <Table stickyHeader size="small" sx={templatesTableSx}>
          <colgroup>
            <col />
            <col style={{ width: ACTION_COLUMN_WIDTH }} />
          </colgroup>

          <TableHead>
            <TableRow>
              <TableCell colSpan={2} sx={headerCellSx}>
                <Typography sx={headerTitleSx}>
                  Templates
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            <TableRow sx={{...addRowSx,height:'56px !important'}}>
              <TableCell sx={{...addInputCellSx,height:'56px !important'}}>
                <TextField
                  autoComplete="off"
                  fullWidth
                  size="small"
                  placeholder="Add new topic"
                  value={newTopic}
                  onChange={event => setNewTopic(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAdd();
                    }

                    if (event.key === 'Escape') {
                      handleClear();
                    }
                  }}
                  sx={inputSx}
                />
              </TableCell>

              <TableCell align="center" sx={addActionCellSx}>
                <Box sx={actionWrapperSx}>
                  <IconButton
                    size="small"
                    aria-label="Add topic"
                    disabled={!newTopic.trim()}
                    onClick={handleAdd}
                    sx={{
                      ...actionIconSx,
                      ...addIconSx
                    }}
                  >
                    <CheckOutlined sx={actionSvgSx} />
                  </IconButton>

                  <IconButton
                    size="small"
                    aria-label="Clear topic"
                    disabled={!newTopic}
                    onClick={handleClear}
                    sx={{
                      ...actionIconSx,
                      ...clearIconSx
                    }}
                  >
                    <ClearOutlined sx={actionSvgSx} />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>

            {topics.map(topic => (
              <TemplateRow
                key={topic.id}
                topic={topic}
                selected={selectedTopicId === topic.id}
                editing={editingTopicId === topic.id}
                editingValue={editingTopicValue}
                onEditingValueChange={setEditingTopicValue}
                onSelect={() => onSelectTopic(topic.id)}
                onEdit={() => handleStartEdit(topic)}
                onSave={() => handleSaveEdit(topic.id)}
                onCancel={handleCancelEdit}
              />
            ))}

            {!topics.length && (
              <TableRow>
                <TableCell colSpan={2} sx={emptyCellSx}>
                  No templates available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

TemplatesPanel.propTypes = {
  topics: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired
    })
  ),
  selectedTopicId: PropTypes.number,
  onSelectTopic: PropTypes.func,
  onAddTopic: PropTypes.func,
  onUpdateTopic: PropTypes.func
};

TemplatesPanel.defaultProps = {
  topics: [],
  selectedTopicId: null,
  onSelectTopic: () => {},
  onAddTopic: () => {},
  onUpdateTopic: () => {}
};

export default TemplatesPanel;